
///
/// EarthBrowser
///
/// Created by Alex Giger on 8/9/2024
/// Copyright (c) 2024 Geodata Labs
///

import { type LatLonAltBox } from "@/earthbrowser/formats/Shapefile"
import { type Geometry } from "@stovokor/shader/Geometry"
import { SweepContext } from "@/earthbrowser/libs/poly2tri"
import { rgba } from "@stovokor/math/color"
import { type cube } from "@stovokor/math/cube"
import { wgs } from "@/earthbrowser/utils/geographic"
import { trig } from "@stovokor/math/math"
import { type sphere } from "@stovokor/math/sphere"
import { vec2 } from "@stovokor/math/vector"
import { DrawMode } from "@stovokor/shader/GLConstants"
import { IndexBuffer } from "@stovokor/shader/IndexBuffer"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { type StovokorContext } from "@stovokor/StovokorContext"

export class PolygonGeometry implements Geometry {
	public color = rgba(1, 1, 0, 0.5)
	public altitude = 30
	public position: VertexAttribute
	public indexBuffer: IndexBuffer
	public vertexBuffer: VertexBuffer
	public mode: DrawMode = DrawMode.Triangles
	public transparency = true
	public bounds: cube
	public boundingSphere: sphere
	public available: boolean = true

	constructor(context: StovokorContext, rings: vec2[][], _bounds: LatLonAltBox, altitude: number = 30) {

		this.position = new VertexAttribute(context, 3)
		this.indexBuffer = new IndexBuffer(context)
		this.vertexBuffer = new VertexBuffer(context, [this.position])

		for(const ring of rings) {
			ring.pop()	// remove the last point which is a duplicate of the first
		}

		const ctx = new SweepContext(this.deduplicated(rings[0]), { cloneArrays: true })
		// rings.slice(1).forEach(ring => ctx.addHole(ring))
		ctx.triangulate()

		const toRad = trig.rad
		const sin = Math.sin
		const cos = Math.cos
		const elev = altitude + wgs.a

		let index = 0
		const triangles = ctx.getTriangles()
		for(const t of triangles) {
			const points = t.getPoints()

			let lon = points[0].x * toRad
			let lat = points[0].y * toRad
			let coslat = cos(lat) * elev
			this.position.addPos(sin(lon) * coslat, sin(lat) * elev, cos(lon) * coslat)

			lon = points[1].x * toRad
			lat = points[1].y * toRad
			coslat = cos(lat) * elev
			this.position.addPos(sin(lon) * coslat, sin(lat) * elev, cos(lon) * coslat)

			lon = points[2].x * toRad
			lat = points[2].y * toRad
			coslat = cos(lat) * elev
			this.position.addPos(sin(lon) * coslat, sin(lat) * elev, cos(lon) * coslat)

			this.indexBuffer.triangle(index, index+1, index+2)
			index += 3
		}
		this.bounds = this.position.bounds
		this.boundingSphere = this.bounds.sphere
	}

	private deduplicated(points: vec2[]): vec2[] {
		const deduped: vec2[] = []
		let last = new vec2(0, 0)
		points.forEach(point => {
			if(point.x != last.x || point.y != last.y) {
				deduped.push(point)
				last = point
			}
		})
		if(deduped.length > 1) {
			const first = deduped[0]
			const last = deduped[deduped.length - 1]
			if(first.x == last.x && first.y == last.y) {
				deduped.pop()
			}
		}
		return deduped
	}

	public destroy(): void {
		this.vertexBuffer.destroy()
		this.indexBuffer.destroy()
	}
}